import React, { useState, useEffect } from 'react';
import { Company, PaymentRecord } from '../types/billing';

interface PaymentFormProps {
  companies: Company[];
  onSave: (payment: PaymentRecord) => Promise<void>;
  onCancel: () => void;
  selectedCompanyId?: string;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ 
  companies, 
  onSave, 
  onCancel, 
  selectedCompanyId 
}) => {
  const [formData, setFormData] = useState({
    companyId: selectedCompanyId || '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    mode: 'Cash' as 'NEFT' | 'IMPS' | 'RTGS' | 'Cash' | 'Cheque' | 'UPI' | 'Other',
    reference: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');

  useEffect(() => {
    if (selectedCompanyId) {
      setFormData(prev => ({ ...prev, companyId: selectedCompanyId }));
    }
  }, [selectedCompanyId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.companyId || !formData.amount) {
      alert('Please fill in all required fields');
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    setIsSubmitting(true);
    setSuccessMessage('');

    try {
      const payment: PaymentRecord = {
        id: '', // Will be generated by Firebase
        companyId: formData.companyId,
        amount: amount,
        date: formData.date,
        mode: formData.mode,
        reference: formData.reference,
        notes: formData.notes
      };

      await onSave(payment);
      setSuccessMessage('Payment recorded successfully!');

      // Auto-close after a brief delay to show success message
      setTimeout(() => {
        onCancel();
      }, 1500);

    } catch (error) {
      console.error('Error saving payment:', error);
      alert('Error saving payment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Record Payment</h3>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Success Message */}
            {successMessage && (
              <div className="p-3 bg-green-100 border border-green-400 text-green-700 rounded-md">
                {successMessage}
              </div>
            )}

            <div>
              <label htmlFor="companyId" className="block text-sm font-medium text-gray-700">
                Company *
              </label>
              <select
                id="companyId"
                name="companyId"
                value={formData.companyId}
                onChange={handleChange}
                required
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-avis-blue focus:border-avis-blue sm:text-sm rounded-md"
              >
                <option value="">Select a company</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
                Amount (₹) *
              </label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                step="0.01"
                min="0"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-avis-blue focus:border-avis-blue sm:text-sm"
                placeholder="Enter amount"
              />
            </div>

            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                Payment Date *
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-avis-blue focus:border-avis-blue sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="mode" className="block text-sm font-medium text-gray-700">
                Payment Method
              </label>
              <select
                id="mode"
                name="mode"
                value={formData.mode}
                onChange={handleChange}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-avis-blue focus:border-avis-blue sm:text-sm rounded-md"
              >
                <option value="Cash">Cash</option>
                <option value="NEFT">NEFT</option>
                <option value="IMPS">IMPS</option>
                <option value="RTGS">RTGS</option>
                <option value="Cheque">Cheque</option>
                <option value="UPI">UPI</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label htmlFor="reference" className="block text-sm font-medium text-gray-700">
                Reference Number
              </label>
              <input
                type="text"
                id="reference"
                name="reference"
                value={formData.reference}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-avis-blue focus:border-avis-blue sm:text-sm"
                placeholder="Transaction/Cheque number"
              />
            </div>

            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                Notes
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-avis-blue focus:border-avis-blue sm:text-sm"
                placeholder="Additional notes about the payment"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-avis-blue"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-avis-blue border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-avis-blue disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : 'Record Payment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PaymentForm;
